/**
 * entityRecordsRegistryStore
 *
 * Module-level Valtio singleton for the entity-records registry. Replaces the
 * former `EntityRecordsRegistryProvider` / React Context layer. There is one
 * registry per app instance — no scoping needed.
 *
 * Reactive state lives in the `proxy(...)` below; subscribers (via `useSnapshot`)
 * re-render granularly when their cache key changes. `misses` holds, per cache
 * key, the single-record reads (`getRecord`) that yielded no record: `absent`
 * when the record does not exist, `failed` when the read threw, each with the
 * time it settled. It lets a reader tell a record that does not exist from one
 * still being read, and holds back a re-read for `MISS_RETRY_MS`. A bucket's
 * misses live and die with the bucket (`subscriberCounts` has its key).
 * Bookkeeping that should not trigger renders (refcounts, grace timers,
 * in-flight fetch keys) lives outside the proxy as plain Maps.
 */
import { proxy } from 'valtio';
import { proxyMap } from 'valtio/utils';
import type { RecordsCacheKey } from '#types/System.RecordsRegistry.types';
import type { EntityRecord } from '#types/System.types';

export const entityRecordsRegistryStore = proxy({
    records: proxyMap<RecordsCacheKey, EntityRecord[]>(),
    loading: proxyMap<RecordsCacheKey, boolean>(),
    errors: proxyMap<RecordsCacheKey, Error | null>(),
    misses: proxyMap<RecordsCacheKey, Record<string, { at: number; outcome: 'absent' | 'failed' }>>(),
});

export const MISS_RETRY_MS = 60_000;

export const subscriberCounts = new Map<RecordsCacheKey, number>();
export const gracePeriodTimers = new Map<RecordsCacheKey, ReturnType<typeof setTimeout>>();
export const lastFetchTimestamps = new Map<RecordsCacheKey, number>();
export const pendingSingleFetches = new Set<string>();
export const pendingUsageRegistrations: Array<{ entityId: string; recordId: string }> = [];

export const cacheKey = (entityId: string, entityGroupId?: string): RecordsCacheKey =>
    `${entityId}:${entityGroupId || ''}`;

export const clearEntityRecordsRegistry = (): void => {
    entityRecordsRegistryStore.records.clear();
    entityRecordsRegistryStore.loading.clear();
    entityRecordsRegistryStore.errors.clear();
    entityRecordsRegistryStore.misses.clear();
    subscriberCounts.clear();
    gracePeriodTimers.forEach((timer) => clearTimeout(timer));
    gracePeriodTimers.clear();
    lastFetchTimestamps.clear();
    pendingSingleFetches.clear();
    pendingUsageRegistrations.length = 0;
};
